"use client";

// Page de présentation publique de Reps : l'adresse qu'on met en description
// de vidéo ou en bio, là où /reps envoie déjà jouer.
//
// Deux partis pris hérités du reste de l'univers Reps :
//   - tutoiement, comme reps_catalog_* et le player, avec BeBot visible pour
//     que la voix ait un visage (cf skill believemy-copy) ;
//   - le bouton principal envoie DIRECTEMENT dans la leçon d'essai, pas vers
//     une inscription : la démo est le meilleur argument de vente du produit,
//     et le mur d'inscription attend déjà à la fin de cette leçon.

import Image from "next/image";
import { motion } from "motion/react";
import { useLocale, useTranslations } from "next-intl";
import { Link } from "@/i18n/navigation";
import Button from "@/components/ui/Button/Button";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import RepsIcon from "@/components/Reps/RepsIcon";
import { REPS_FREE_KEYS_PER_DAY } from "@/lib/reps/access";
import { appStoreBadge } from "@/lib/reps/mobileApp";

const HOW_ITEMS = [
    { key: "short", icon: "stopwatch-bold-duotone" },
    { key: "write", icon: "keyboard-bold-duotone" },
    { key: "streak", icon: "fire-bold-duotone" },
];

// Le prix arrive de l'environnement au format français ("4,99"). L'anglais
// écrit le séparateur décimal avec un point : sans cette conversion, la page
// anglaise afficherait "€4,99". La règle vit ici, dans le seul composant qui
// affiche ce prix, plutôt que recopiée dans chaque page qui le monte.
function localizedPrice(raw, locale) {
    if (!raw) return null;
    return locale === "fr" ? raw : String(raw).replace(",", ".");
}

// Fondu + micro-translation, la seule signature d'animation autorisée hors
// gamification. Le viewport once évite qu'une section rejoue à chaque passage.
function Reveal({ children, delay = 0, className }) {
    return (
        <motion.div
            className={className}
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.25, ease: "easeOut", delay }}
        >
            {children}
        </motion.div>
    );
}

function Section({ title, children, className }) {
    return (
        <section className={`mt-16 md:mt-24 ${className || ""}`}>
            <Reveal>
                <h2 className="text-center text-2xl font-bold text-gray-900 dark:text-white md:text-3xl">
                    {title}
                </h2>
            </Reveal>
            {children}
        </section>
    );
}

export default function RepsLanding({
    tracks = [],
    trialLessonSlug = null,
    plusPrice = null,
    appStoreUrl = null,
    // Membre connecté qui n'a encore rien commencé : même page, deux retouches
    // qui comptent. Il a déjà un compte (le bouton d'inscription n'a plus de
    // sens, et le mot juste devient « commencer », pas « essayer »), et s'il
    // est abonné il ne doit pas croiser une grille de prix : proposer Believemy
    // Plus à 4,99 € à quelqu'un qui paie déjà Believemy Pro est au mieux
    // inutile.
    isMember = false,
    showPricing = true,
}) {
    const t = useTranslations();
    const locale = useLocale();
    const badge = appStoreBadge(locale);
    const price = localizedPrice(plusPrice, locale);

    // Aucun parcours publié, ou back injoignable : pas de bouton d'essai du
    // tout. Il ne peut mener nulle part, et cette page EST /reps pour un
    // visiteur déconnecté : l'y renvoyer boucherait sur elle-même.
    const tryButton = trialLessonSlug ? (
        <Link
            href={{
                pathname: "/reps/[lessonSlug]",
                params: { lessonSlug: trialLessonSlug },
            }}
        >
            <Button type="primary" large>
                {t(isMember ? "reps_landing_cta_start" : "reps_landing_cta_try")}
            </Button>
        </Link>
    ) : null;

    const signupButton = isMember ? null : (
        <Link href="/signup">
            <Button type={tryButton ? "secondary" : "primary"} large>
                {t("reps_landing_cta_signup")}
            </Button>
        </Link>
    );

    return (
        <div className="mx-auto max-w-5xl px-5 py-12 md:py-20">
            {/* Hero */}
            <Reveal>
                <div className="text-center">
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-orange-50 px-3 py-1 text-xs font-semibold text-orange-600 dark:bg-orange-500/10 dark:text-orange-400">
                        <SolarIcon name="bolt-bold" className="text-sm" />
                        Reps · {t("reps_landing_badge")}
                    </span>

                    <h1 className="mx-auto mt-5 max-w-3xl text-3xl font-bold leading-tight text-gray-900 dark:text-white md:text-5xl md:leading-tight">
                        {t("reps_landing_title")}
                    </h1>

                    <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-gray-500 dark:text-grayDark-400 md:text-lg">
                        {t("reps_landing_subtitle")}
                    </p>

                    <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
                        {tryButton}
                        {signupButton}
                    </div>

                    {tryButton && !isMember && (
                        <p className="mb-0 mt-3 text-xs text-gray-400">
                            {t("reps_landing_cta_try_note")}
                        </p>
                    )}
                </div>
            </Reveal>

            {/* Pourquoi. BeBot porte le tutoiement de toute la page. */}
            <Reveal delay={0.05}>
                <div className="mt-16 flex flex-col items-center gap-6 rounded-3xl border border-gray-100 bg-white p-6 dark:border-white/10 dark:bg-white/[0.03] md:mt-24 md:flex-row md:gap-8 md:p-10">
                    <Image
                        src="/images/bebot/bebot-reps.png"
                        alt=""
                        width={140}
                        height={140}
                        className="h-28 w-28 shrink-0 object-contain md:h-36 md:w-36"
                    />
                    <div className="min-w-0 text-center md:text-left">
                        <h2 className="text-xl font-bold text-gray-900 dark:text-white md:text-2xl">
                            {t("reps_landing_why_title")}
                        </h2>
                        <p className="mb-0 mt-3 text-[15px] leading-relaxed text-gray-600 dark:text-grayDark-300">
                            {t("reps_landing_why_body")}
                        </p>
                        <p className="mb-0 mt-3 text-sm leading-relaxed text-gray-400">
                            {t("reps_landing_why_honest")}
                        </p>
                    </div>
                </div>
            </Reveal>

            {/* Comment ça marche */}
            <Section title={t("reps_landing_how_title")}>
                <div className="mt-8 grid gap-4 md:grid-cols-3">
                    {HOW_ITEMS.map((item, i) => (
                        <Reveal key={item.key} delay={Math.min(i * 0.05, 0.15)}>
                            <div className="h-full rounded-2xl border border-gray-100 bg-white p-6 dark:border-white/10 dark:bg-white/[0.03]">
                                <SolarIcon
                                    name={item.icon}
                                    className="text-3xl text-primary"
                                />
                                <h3 className="mt-4 text-base font-semibold text-gray-900 dark:text-white">
                                    {t(`reps_landing_how_${item.key}_title`)}
                                </h3>
                                <p className="mb-0 mt-1.5 text-sm leading-relaxed text-gray-500 dark:text-grayDark-400">
                                    {t(`reps_landing_how_${item.key}_desc`)}
                                </p>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </Section>

            {/* Parcours réellement publiés */}
            {tracks.length > 0 && (
                <Section title={t("reps_landing_tracks_title")}>
                    <div className="mx-auto mt-8 max-w-2xl space-y-3">
                        {tracks.map((track, i) => (
                            <Reveal
                                key={track.code || track.slug}
                                delay={Math.min(i * 0.04, 0.16)}
                            >
                                <Link
                                    href={{
                                        pathname: "/reps/path/[trackSlug]",
                                        params: { trackSlug: track.slug },
                                    }}
                                    className="flex items-center gap-4 rounded-2xl border border-gray-100 bg-white p-4 transition-colors hover:border-orange-200 hover:bg-orange-50/30 dark:border-white/10 dark:bg-white/[0.03] dark:hover:border-orange-500/30"
                                >
                                    <RepsIcon
                                        size={48}
                                        src={track.icon || null}
                                        color={track.color || null}
                                    />
                                    <span className="min-w-0 flex-1">
                                        <span className="block text-[15px] font-semibold text-gray-900 dark:text-white">
                                            {track.title}
                                        </span>
                                        {track.subtitle ? (
                                            <span className="mt-0.5 line-clamp-2 block text-[13px] leading-snug text-gray-500 dark:text-grayDark-400">
                                                {track.subtitle}
                                            </span>
                                        ) : null}
                                        <span className="mt-1 block text-xs text-gray-400">
                                            {t("reps_landing_tracks_meta", {
                                                units: track.unitsCount || 0,
                                                lessons: track.lessonsCount || 0,
                                            })}
                                        </span>
                                    </span>
                                    <SolarIcon
                                        name="alt-arrow-right-linear"
                                        className="shrink-0 text-lg text-gray-300 dark:text-grayDark-600"
                                    />
                                </Link>
                            </Reveal>
                        ))}
                    </div>
                    <Reveal delay={0.1}>
                        <p className="mb-0 mt-4 text-center text-sm text-gray-400">
                            {t("reps_landing_tracks_soon")}
                        </p>
                    </Reveal>
                </Section>
            )}

            {/* Prix. Masqué pour qui a déjà les clés illimitées. */}
            {showPricing && (
            <Section title={t("reps_landing_price_title")}>
                <div className="mx-auto mt-8 grid max-w-3xl gap-4 md:grid-cols-2">
                    <Reveal>
                        <div className="h-full rounded-2xl border border-gray-100 bg-white p-6 dark:border-white/10 dark:bg-white/[0.03]">
                            <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                                {t("reps_landing_price_free_title")}
                            </h3>
                            <p className="mb-0 mt-1.5 text-sm leading-relaxed text-gray-500 dark:text-grayDark-400">
                                {t("reps_landing_price_free_desc", {
                                    count: REPS_FREE_KEYS_PER_DAY,
                                })}
                            </p>
                        </div>
                    </Reveal>
                    <Reveal delay={0.05}>
                        <div className="h-full rounded-2xl border border-orange-200 bg-orange-50/40 p-6 dark:border-orange-500/30 dark:bg-orange-500/[0.06]">
                            <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                                {t("reps_landing_price_plus_title", { price })}
                            </h3>
                            <p className="mb-0 mt-1.5 text-sm leading-relaxed text-gray-500 dark:text-grayDark-400">
                                {t("reps_landing_price_plus_desc")}
                            </p>
                            <Link
                                href={{
                                    pathname: "/pricing",
                                    query: { checkout: "essential" },
                                }}
                                className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
                            >
                                {t("reps_landing_price_link")}
                                <SolarIcon
                                    name="alt-arrow-right-linear"
                                    className="text-base"
                                />
                            </Link>
                        </div>
                    </Reveal>
                </div>
            </Section>
            )}

            {/* L'app : rien tant qu'elle n'est pas publiée (URL vide = section
                absente, pas de "bientôt disponible" qui vieillit mal). */}
            {appStoreUrl && (
                <Reveal>
                    <div className="mt-16 flex flex-col items-center gap-4 rounded-3xl border border-gray-100 bg-white p-8 text-center dark:border-white/10 dark:bg-white/[0.03] md:mt-24">
                        <h2 className="text-xl font-bold text-gray-900 dark:text-white md:text-2xl">
                            {t("reps_landing_app_title")}
                        </h2>
                        <p className="mb-0 max-w-md text-sm leading-relaxed text-gray-500 dark:text-grayDark-400">
                            {t("reps_landing_app_desc")}
                        </p>
                        <a
                            href={appStoreUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-1 inline-block"
                        >
                            <Image
                                src={badge.black}
                                width={badge.width}
                                height={badge.height}
                                alt={t("reps_app_badge_alt")}
                                className="dark:hidden"
                            />
                            <Image
                                src={badge.white}
                                width={badge.width}
                                height={badge.height}
                                alt={t("reps_app_badge_alt")}
                                className="hidden dark:block"
                            />
                        </a>
                    </div>
                </Reveal>
            )}

            {/* Dernier appel */}
            <Reveal>
                <div className="mt-16 text-center md:mt-24">
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white md:text-3xl">
                        {t("reps_landing_final_title")}
                    </h2>
                    <p className="mx-auto mb-0 mt-3 max-w-md text-[15px] leading-relaxed text-gray-500 dark:text-grayDark-400">
                        {t("reps_landing_final_desc")}
                    </p>
                    <div className="mt-7 flex justify-center">
                        {tryButton || signupButton}
                    </div>
                </div>
            </Reveal>
        </div>
    );
}
